import React, { useState, useEffect, useRef } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import { getProducts } from '../../../api/products';
import { useUrlDataContext } from '../lib/UrlDataContext';
import './SearchProducts.scss';
import Loader from '../../General/Loader';
import classnames from 'classnames';
import { getShopProductUrl, getSmartImage } from '../../../helpers/helpers';
import { useActionContext } from '../lib/ActionContext';

export const SearchProducts = () => {
  const { currentUrlData, isCurrentUrlBlocked } = useUrlDataContext();
  const shortProductName = (currentUrlData.productName ?? '')
    .trim()
    .split(' ')
    .slice(0, 3)
    .join(' ');
  const searchTermFromCurrentUrlData = `${currentUrlData.brandName} ${shortProductName}`.replaceAll('null', '').trim();
  const [searchTerm, setSearchTerm] = useState(searchTermFromCurrentUrlData);
  const [debouncedValue, setDebouncedValue] = useState('');
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const isMounted = useRef(true);
  const { setProductId, setView } = useActionContext();

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSearchTerm(searchTermFromCurrentUrlData);
  }, [searchTermFromCurrentUrlData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isMounted.current) {
        setDebouncedValue(searchTerm);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedValue.length <= 2) {
      setProducts([]);
      return undefined;
    }

    let cancelled = false;

    const doSearch = async () => {
      setIsLoading(true);

      try {
        const { products } = await getProducts({ query: debouncedValue, pageSize: 40 });

        if (!cancelled) {
          setProducts(products);
        }
      } catch {
        if (!cancelled) {
          window.ALERT.error('Failed to search products. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    doSearch();

    return () => {
      cancelled = true;
    };
  }, [debouncedValue]);

  const handleChange = e => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className='url-data-search-products'>
      <div className='search-input-wrapper'>
        <IoSearchOutline className='search-icon' size={20} />
        <input type='text' placeholder='Search for a product' value={searchTerm} onChange={handleChange} ref={inputRef} />

        <Loader variant='circle' className={classnames('loading-icon', { visible: isLoading })} size={24} />
      </div>

      <ul className='search-results'>
        {products.map(product => (
          <li key={product.id} className='search-result'>
            <img src={getSmartImage(product.image)} alt={product.name} />

            <div>
              <p className='product-brand'>{product.brand}</p>
              <p className='product-title'>
                <a href={getShopProductUrl(product.id)} target='_blank' rel='noopener noreferrer'>
                  {product.title}
                </a>
              </p>
              <p className='product-promoters'>{product.totalMentionCount ?? 0} promoters</p>
            </div>

            <button
              onClick={() => {
                setProductId(product.id);
                setView('edit-product');
              }}
              disabled={isCurrentUrlBlocked}
            >
              Match
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
